/**
 * **A client's phone** — the photographed iPhone frame with live HTML behind its glass, as `/konto`
 * draws the trainer's brand on it and `/rezerwacje` draws the client's booking screen.
 *
 * Promoted out of `pages/account` on its second consumer. The frame is a PNG
 * (`public/account/iphone-frame.png`) whose display area is a transparent hole; the screen is drawn
 * FIRST and half a percent larger than that hole, and the frame lies over it, so the bezel itself
 * masks the glass. The dynamic island is part of what was cut out, so it is drawn ABOVE the frame.
 * Everything inside the screen is sized in `cqw` (the screen is an inline-size container), so the
 * whole phone scales with whatever height the caller gives it.
 *
 * The screen wears the client app's own colours (`--phone-*`, theme-independent on purpose): it is
 * the CLIENT's phone, whatever theme the trainer reads it in.
 *
 * It is decoration by default: `aria-hidden` over the drawing, and the caller says in one
 * visually-hidden sentence what it shows. A screen the trainer can really use (the public form
 * builder's question runner) passes `label` instead, and the phone is a named region. The frame's
 * size is the caller's (`className` sets its height); the aspect ratio is the PNG's own.
 */

import type { CSSProperties, ReactNode } from 'react';

import styles from './PhoneFrame.module.css';

interface PhoneFrameProps {
  /** Sets the phone's HEIGHT; the width follows the frame's own aspect ratio. */
  readonly className?: string;
  /** Custom properties handed to the screen, such as a brand's paint. */
  readonly style?: CSSProperties;
  /** Names the phone as a region, for a screen that is interactive rather than a picture. */
  readonly label?: string;
  readonly children: ReactNode;
}

export function PhoneFrame({ className, style, label, children }: PhoneFrameProps) {
  const a11y =
    label === undefined ? ({ 'aria-hidden': true } as const) : ({ role: 'region', 'aria-label': label } as const);
  return (
    <div className={`${String(styles.phone)} ${className ?? ''}`} style={style} {...a11y}>
      <span className={styles.glass} />
      <div className={styles.screen}>
        <div className={styles.status}>
          <span>9:41</span>
        </div>
        {children}
      </div>
      <img className={styles.frame} src="/account/iphone-frame.png" alt="" decoding="async" />
      <span className={styles.island} />
    </div>
  );
}
